const DEFAULT_SITE_NAME = 'Glace Confeitaria';
const DEFAULT_SHORT_NAME = 'Glace';
const DEFAULT_DESCRIPTION =
  'Brownies artesanais, presentes delicados e encomendas sob medida para abrir, pedir e retomar o carrinho com rapidez.';
// Keep this asset and its dimensions aligned with public/index.html for non-JS crawlers.
const DEFAULT_SHARE_IMAGE = '/images/share-glace-v1.png';
const DEFAULT_SHARE_IMAGE_ALT = 'Símbolo da Glacê Confeitaria';
const DEFAULT_THEME_COLOR = '#751215';
const DEFAULT_BACKGROUND_COLOR = '#FFF8F5';
const DEFAULT_LOCALE = 'pt-BR';

const normalizePathname = (value) => {
  const decodedValue = String(value || '/').trim() || '/';
  let pathname = decodedValue;

  try {
    pathname = decodeURIComponent(decodedValue);
  } catch (error) {
    pathname = decodedValue;
  }

  if (!pathname.startsWith('/')) {
    pathname = `/${pathname}`;
  }

  if (pathname.length > 1) {
    pathname = pathname.replace(/\/+$/g, '');
  }

  return pathname || '/';
};

const normalizeSlug = (value) =>
  String(value || '')
    .trim()
    .toLowerCase()
    .replace(/^\/+|\/+$/g, '');

const resolveAbsoluteUrl = (value, baseUrl) => {
  const normalizedValue = String(value || '').trim();

  if (!normalizedValue) {
    return '';
  }

  try {
    return new URL(normalizedValue, baseUrl).toString();
  } catch (error) {
    return normalizedValue;
  }
};

const buildSiteDefaults = (experience, siteUrl) => {
  const branding = experience?.data?.branding || {};
  const tokens = experience?.data?.theme?.tokens || {};
  const homePage = experience?.data?.pages?.home || {};
  const homeSeo = homePage?.seo || {};
  const siteName = branding?.displayName || DEFAULT_SITE_NAME;
  const shortName = siteName.split(' ')[0] || DEFAULT_SHORT_NAME;
  const description = homeSeo?.description || branding?.tagline || DEFAULT_DESCRIPTION;

  return {
    siteName,
    shortName,
    locale: experience?.instance?.locale || DEFAULT_LOCALE,
    description,
    image: resolveAbsoluteUrl(DEFAULT_SHARE_IMAGE, siteUrl),
    imageAlt: DEFAULT_SHARE_IMAGE_ALT,
    imageWidth: 512,
    imageHeight: 512,
    imageType: 'image/png',
    themeColor: tokens?.colorPrimary || DEFAULT_THEME_COLOR,
    backgroundColor: tokens?.colorSurface || DEFAULT_BACKGROUND_COLOR,
  };
};

const findConfiguredPage = (experience, pathname) => {
  const normalizedPath = normalizePathname(pathname);

  if (normalizedPath === '/' || normalizedPath === '/cardapio' || normalizedPath === '/carrinho') {
    return null;
  }

  const entries = Array.isArray(experience?.data?.pages?.entries) ? experience.data.pages.entries : [];
  const pathnameSlug = normalizeSlug(normalizedPath);

  return entries.find((entry) => normalizeSlug(entry?.slug) === pathnameSlug) || null;
};

export const buildRouteMetadata = ({ pathname, experience, siteUrl }) => {
  const normalizedPath = normalizePathname(pathname);
  const defaults = buildSiteDefaults(experience, siteUrl);
  const configuredPage = findConfiguredPage(experience, normalizedPath);

  let title = defaults.siteName;
  let description = defaults.description;
  let type = 'website';
  let shareText =
    'Abra o card\u00e1pio da Glace, monte seu pedido e siga para o WhatsApp com tudo pronto.';

  if (normalizedPath === '/cardapio') {
    title = `Card\u00e1pio | ${defaults.siteName}`;
    description =
      'Veja brownies artesanais, presentes e encomendas sob medida com uma experi\u00eancia r\u00e1pida para abrir e pedir.';
    shareText = 'Veja o card\u00e1pio da Glace e escolha seus brownies e presentes favoritos.';
  } else if (normalizedPath === '/carrinho') {
    title = `Carrinho | ${defaults.siteName}`;
    description =
      'Retome seu pedido, revise os itens e finalize o atendimento da Glace pelo WhatsApp em poucos toques.';
    shareText = 'Retome seu pedido na Glace e finalize tudo com mais rapidez.';
  } else if (configuredPage) {
    title = configuredPage?.seo?.title || configuredPage?.title || defaults.siteName;
    description =
      configuredPage?.seo?.description ||
      configuredPage?.hero?.supportingText ||
      defaults.description;
    type = 'article';
    shareText =
      configuredPage?.hero?.supportingText ||
      configuredPage?.seo?.description ||
      defaults.description;
  }

  const absoluteUrl = resolveAbsoluteUrl(normalizedPath, siteUrl);

  return {
    ...defaults,
    title,
    description,
    type,
    url: absoluteUrl,
    canonicalUrl: absoluteUrl,
    shareText,
  };
};
